/* Rice variant UI; the shared tier selector and product form retain cart ownership. */
(() => {
  if (customElements.get('kojic-offer-controller')) return;
  class KojicOfferController extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.start = () => this.init();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.start, {once:true});
      else this.init();
    }
    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.start);
      this.controller?.abort();
      this.ready = false;
    }
    init() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.data = JSON.parse(this.querySelector('[data-serum-data]').textContent);
      this.form = document.getElementById(this.dataset.formId);
      this.section = this.closest('.shopify-section');
      this.tiers = this.section?.querySelector('[data-pdp-tiers]');
      if (!this.form || !this.tiers) return;
      this.selected = this.data.variants.find(v => String(v.variant.id) === this.dataset.selectedVariant) || this.data.variants[0];
      this.subscriptionOn = true;
      this.subscriptionBox = this.section.querySelector('[data-rice-subscription]');
      this.subscriptionToggle = this.subscriptionBox?.querySelector('button');
      this.subscriptionToggle?.addEventListener('click', () => {
        if (this.selected.variant.requires_selling_plan) return;
        this.subscriptionOn = !this.subscriptionOn;
        this.paint();
      }, {signal});
      this.querySelectorAll('[data-serum-variant]').forEach(button => button.addEventListener('click', () => {
        const next = this.data.variants.find(v => String(v.variant.id) === button.dataset.serumVariant);
        if (!next) return;
        this.selected = next;
        this.paint();
        this.querySelector('details').open = false;
        this.querySelector('summary').focus({preventScroll:true});
        const url = new URL(location.href);
        url.searchParams.set('variant', next.variant.id);
        history.replaceState(history.state, '', url);
      }, {signal}));
      this.tiers.addEventListener('change', () => this.updateAvailability(), {signal});
      // Keep the shared theme transport, but send one unambiguous indexed items payload.
      // Mixing a top-level id with items[] gifts can omit the paid item in Shopify.
      this.form.addEventListener('formdata', event => this.cartData(event.formData), {signal});
      this.form.addEventListener('submit', event => {
        if (!this.available()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.updateAvailability();
        }
      }, {capture:true, signal});
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          this.querySelector('details').open = false;
          this.querySelector('summary').focus();
        }
      }, {signal});
      this.paint();
    }
    cartData(data) {
      const id = data.get('id') || String(this.selected.variant.id);
      const quantity = data.get('quantity') || '1';
      const plan = data.get('selling_plan');
      const properties = [...data.entries()].filter(([key]) => /^properties\[.+\]$/.test(key));
      const gifts = [];
      this.tiers.querySelectorAll('.product-form-28__gift-input:not(:disabled)[name="items[][id]"]').forEach(input => {
        if (input.value) gifts.push(input.value);
      });
      [...new Set(data.keys())].forEach(key => {
        if (['id','quantity','selling_plan'].includes(key) || key.startsWith('items[') || key.startsWith('properties[')) data.delete(key);
      });
      data.set('items[0][id]', id);
      data.set('items[0][quantity]', quantity);
      if (plan) data.set('items[0][selling_plan]', plan);
      properties.forEach(([key,value]) => data.set(`items[0][properties][${key.slice(11,-1)}]`, value));
      [...new Set(gifts)].forEach((gift,index) => {
        data.set(`items[${index+1}][id]`, gift);
        data.set(`items[${index+1}][quantity]`, '1');
        data.set(`items[${index+1}][properties][_gift]`, '1');
      });
    }
    available() {
      const bogo = this.tiers.querySelector('[data-tier="bogo"]');
      return this.selected.variant.available && this.data.giftcardAvailable &&
        (!bogo?.querySelector('.product-form-28__tier-input').checked || (this.selected.bonus.available && this.data.fixedGiftAvailable));
    }
    updateAvailability() {
      const variant = this.selected.variant;
      this.form.dispatchEvent(new CustomEvent('variant:changed', {bubbles:true, detail:{variant}}));
      if (!this.available()) {
        this.form.querySelectorAll('[data-product-add-to-cart-button]').forEach(button => {
          button.disabled = true;
          const content = button.querySelector('.loader-button__text') || button;
          content.textContent = window.themeVariables?.strings?.productFormSoldOut || 'Sold Out';
        });
      }
    }
    paint() {
      const selected = this.selected;
      const hasPlan = !!selected.subscription.id;
      const subscribed = hasPlan && (this.subscriptionOn || selected.variant.requires_selling_plan);
      if (this.subscriptionBox) this.subscriptionBox.hidden = !hasPlan;
      if (this.subscriptionToggle) {
        this.subscriptionToggle.setAttribute('aria-checked', String(subscribed));
        this.subscriptionToggle.disabled = selected.variant.requires_selling_plan;
      }
      let planInput = this.form.querySelector('input[data-rice-plan]') || this.form.querySelector('input[data-auto-selling-plan]');
      if (!planInput && hasPlan) {
        planInput = document.createElement('input');
        planInput.type = 'hidden';
        planInput.name = 'selling_plan';
        this.form.append(planInput);
      }
      if (planInput) {
        planInput.dataset.ricePlan = '';
        planInput.value = subscribed ? selected.subscription.id : '';
        planInput.disabled = !subscribed;
      }
      const template = this.querySelector(`[data-serum-card="${selected.variant.id}"]`);
      this.querySelector('[data-serum-current]').replaceChildren(template.content.cloneNode(true));
      this.querySelectorAll('[data-serum-variant]').forEach(button => {
        const active = button.dataset.serumVariant === String(selected.variant.id);
        button.hidden = active;
        button.setAttribute('aria-pressed', String(active));
      });
      this.section.dataset.riceSerum = selected.black ? 'black' : 'white';
      const bonusIds = this.data.variants.map(v => String(v.bonus.id));
      this.tiers.querySelectorAll('.product-form-28__tier-input').forEach(input => {
        input.value = selected.variant.id;
        input.disabled = false;
      });
      this.tiers.querySelectorAll('.product-form-28__gift-input').forEach(input => {
        if (bonusIds.includes(input.value) && /\[id\]$|^properties\[/.test(input.name)) input.value = selected.bonus.id;
      });
      const bogo = this.tiers.querySelector('[data-tier="bogo"]');
      const bonusRow = bogo?.querySelector('[data-rice-bonus]');
      if (bonusRow) {
        bonusRow.querySelector('.product-form-28__perk-text').textContent = selected.bonus.title;
        bonusRow.querySelector('.product-form-28__perk-price').textContent = selected.bonus.value;
        const image = bonusRow.querySelector('img');
        if (image && selected.bonus.image) {
          image.removeAttribute('srcset');
          image.src = selected.bonus.image;
        }
      }
      const price = subscribed ? selected.subscription.price : selected.price;
      const compare = subscribed ? selected.subscription.compare : selected.compare;
      [['[data-tier1-price]',price],['[data-tier2-price]',price],['[data-tier1-compare]',compare],['[data-tier2-compare]',selected.bogoCompare]].forEach(([selector,value]) => {
        const element = this.tiers.querySelector(selector);
        if (!element) return;
        element.textContent = value;
        const kind = selector.includes('compare') ? 'compare' : 'price';
        element.setAttribute(`data-${kind}-plain`, value);
        element.setAttribute(`data-${kind}-sub`, value);
      });
      this.tiers.querySelector('.product-form-28__tier-input:checked')?.dispatchEvent(new Event('change',{bubbles:true}));
      this.updateAvailability();
    }
  }
  customElements.define('kojic-offer-controller', KojicOfferController);
})();
